import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { pushApi } from "api/endpoints/push.api";
import { pushManagementApi } from "api/endpoints/push_management.api";
import {
  PushMessage,
  SendPushRequest,
  Campaign,
  CampaignCreateRequest,
  Segment,
  SegmentCreateRequest,
  PushHistoryFilter,
  PushStatistics,
  PushHistoryGroupedItem
} from "types/push.types";
import {
  PushTemplate,
  PushTemplateCreateRequest,
  PushTemplateUpdateRequest,
  EventPushTrigger,
  EventTriggerCreateRequest,
  EventTriggerUpdateRequest,
  RecurringSchedule,
  RecurringScheduleCreateRequest,
  RecurringScheduleUpdateRequest,
  SchedulePreset,
  SchedulePresetCreateRequest,
  TriggerPreset,
  TriggerPresetCreateRequest,
} from "types/push_management.types";

interface PushState {
  messages: PushMessage[];
  currentMessage: PushMessage | null;
  statistics: PushStatistics | null;
  total: number;
  campaigns: Campaign[];
  currentCampaign: Campaign | null;
  segments: Segment[];
  currentSegment: Segment | null;
  templates: PushTemplate[];
  currentTemplate: PushTemplate | null;
  triggers: EventPushTrigger[];
  currentTrigger: EventPushTrigger | null;
  schedules: RecurringSchedule[];
  currentSchedule: RecurringSchedule | null;
  groupedItems: PushHistoryGroupedItem[];
  groupedTotal: number;
  presets: SchedulePreset[];
  currentPreset: SchedulePreset | null;
  triggerPresets: TriggerPreset[];
  currentTriggerPreset: TriggerPreset | null;
  isLoading: boolean;
  isSending: boolean;
  error: string | null;
}

const initialState: PushState = {
  messages: [],
  currentMessage: null,
  statistics: null,
  total: 0,

  campaigns: [],
  currentCampaign: null,

  segments: [],
  currentSegment: null,

  templates: [],
  currentTemplate: null,

  triggers: [],
  currentTrigger: null,

  schedules: [],
  currentSchedule: null,

  groupedItems: [],
  groupedTotal: 0,

  presets: [],
  currentPreset: null,

  triggerPresets: [],
  currentTriggerPreset: null,

  isLoading: false,
  isSending: false,
  error: null,
};

export const sendPush = createAsyncThunk(
  "push/send",
  async (data: SendPushRequest) => {
    const response = await pushApi.send(data);
    return response;
  }
);

export const fetchPushHistory = createAsyncThunk(
  "push/fetchHistory",
  async (filter?: PushHistoryFilter) => {
    const response = await pushApi.getHistory(filter);
    return response;
  }
);

export const fetchPushStatus = createAsyncThunk(
  "push/fetchStatus",
  async (messageId: string) => {
    const response = await pushApi.getStatus(messageId);
    return response;
  }
);

export const cancelScheduledPush = createAsyncThunk(
  "push/cancelScheduled",
  async (messageId: string) => {
    const response = await pushManagementApi.cancelScheduledPush(messageId);
    return { messageId, ...response };
  }
);

export const fetchPushHistoryGrouped = createAsyncThunk(
  'push/fetchHistoryGrouped',
  async (filter: PushHistoryFilter | undefined, { signal }) => {
    const response = await pushApi.getHistoryGrouped(filter, signal);
    return response;
  }
);

export const fetchCampaigns = createAsyncThunk(
  "push/fetchCampaigns",
  async ({
    appId,
    limit = 20,
    offset = 0,
  }: {
    appId?: number;
    limit?: number;
    offset?: number;
  }) => {
    const response = await pushApi.listCampaigns(appId, limit, offset);
    return response;
  }
);

export const fetchCampaign = createAsyncThunk(
  "push/fetchCampaign",
  async (id: number) => {
    const response = await pushApi.getCampaign(id);
    return response;
  }
);

export const createCampaign = createAsyncThunk(
  "push/createCampaign",
  async (data: CampaignCreateRequest) => {
    const response = await pushApi.createCampaign(data);
    return response;
  }
);

export const updateCampaign = createAsyncThunk(
  "push/updateCampaign",
  async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<CampaignCreateRequest>;
  }) => {
    const response = await pushApi.updateCampaign(id, data);
    return response;
  }
);

export const deleteCampaign = createAsyncThunk(
  "push/deleteCampaign",
  async (id: number) => {
    await pushApi.deleteCampaign(id);
    return id;
  }
);

export const fetchSegments = createAsyncThunk(
  "push/fetchSegments",
  async ({
    appId,
    limit = 20,
    offset = 0,
  }: {
    appId?: number;
    limit?: number;
    offset?: number;
  }) => {
    const response = await pushApi.listSegments(appId, limit, offset);
    return response;
  }
);

export const fetchSegment = createAsyncThunk(
  "push/fetchSegment",
  async (id: number) => {
    const response = await pushApi.getSegment(id);
    return response;
  }
);

export const createSegment = createAsyncThunk(
  "push/createSegment",
  async (data: SegmentCreateRequest) => {
    const response = await pushApi.createSegment(data);
    return response;
  }
);

export const testSegment = createAsyncThunk(
  "push/testSegment",
  async (data: SegmentCreateRequest) => {
    const response = await pushApi.testSegment(data);
    return response;
  }
);

export const updateSegment = createAsyncThunk(
  "push/updateSegment",
  async ({ id, data }: { id: number; data: Partial<SegmentCreateRequest> }) => {
    const response = await pushApi.updateSegment(id, data);
    return response;
  }
);

export const deleteSegment = createAsyncThunk(
  "push/deleteSegment",
  async (id: number) => {
    await pushApi.deleteSegment(id);
    return id;
  }
);

export const fetchTemplates = createAsyncThunk(
  "push/fetchTemplates",
  async (appId: number) => {
    const response = await pushManagementApi.templates.list(appId);
    return response;
  }
);

export const fetchTemplate = createAsyncThunk(
  "push/fetchTemplate",
  async (id: number) => {
    const response = await pushManagementApi.templates.get(id);
    return response;
  }
);

export const createTemplate = createAsyncThunk(
  "push/createTemplate",
  async (data: PushTemplateCreateRequest) => {
    const response = await pushManagementApi.templates.create(data);
    return response;
  }
);

export const updateTemplate = createAsyncThunk(
  "push/updateTemplate",
  async ({ id, data }: { id: number; data: PushTemplateUpdateRequest }) => {
    const response = await pushManagementApi.templates.update(id, data);
    return { id, ...response };
  }
);

export const deleteTemplate = createAsyncThunk(
  "push/deleteTemplate",
  async (id: number) => {
    await pushManagementApi.templates.delete(id);
    return id;
  }
);

export const fetchTriggers = createAsyncThunk(
  "push/fetchTriggers",
  async (appId: number) => {
    const response = await pushManagementApi.triggers.list(appId);
    return response;
  }
);

export const fetchTrigger = createAsyncThunk(
  "push/fetchTrigger",
  async (id: number) => {
    const response = await pushManagementApi.triggers.get(id);
    return response;
  }
);

export const createTrigger = createAsyncThunk(
  "push/createTrigger",
  async (data: EventTriggerCreateRequest) => {
    const response = await pushManagementApi.triggers.create(data);
    return response;
  }
);

export const updateTrigger = createAsyncThunk(
  "push/updateTrigger",
  async ({ id, data }: { id: number; data: EventTriggerUpdateRequest }) => {
    const response = await pushManagementApi.triggers.update(id, data);
    return { id, ...response };
  }
);

export const deleteTrigger = createAsyncThunk(
  "push/deleteTrigger",
  async (id: number) => {
    await pushManagementApi.triggers.delete(id);
    return id;
  }
);

export const fetchSchedules = createAsyncThunk(
  "push/fetchSchedules",
  async (appId: number) => {
    const response = await pushManagementApi.schedules.list(appId);
    return response;
  }
);

export const fetchSchedule = createAsyncThunk(
  "push/fetchSchedule",
  async (id: number) => {
    const response = await pushManagementApi.schedules.get(id);
    return response;
  }
);

export const createSchedule = createAsyncThunk(
  "push/createSchedule",
  async (data: RecurringScheduleCreateRequest) => {
    const response = await pushManagementApi.schedules.create(data);
    return response;
  }
);

export const updateSchedule = createAsyncThunk(
  "push/updateSchedule",
  async ({
    id,
    data,
  }: {
    id: number;
    data: RecurringScheduleUpdateRequest;
  }) => {
    const response = await pushManagementApi.schedules.update(id, data);
    return { id, ...response };
  }
);

export const deleteSchedule = createAsyncThunk(
  "push/deleteSchedule",
  async (id: number) => {
    await pushManagementApi.schedules.delete(id);
    return id;
  }
);

export const fetchPresets = createAsyncThunk(
  "push/fetchPresets",
  async () => {
    const response = await pushManagementApi.presets.list();
    return response;
  }
);

export const fetchPreset = createAsyncThunk(
  "push/fetchPreset",
  async (id: number) => {
    const response = await pushManagementApi.presets.get(id);
    return response;
  }
);

export const createPreset = createAsyncThunk(
  "push/createPreset",
  async (data: SchedulePresetCreateRequest) => {
    const response = await pushManagementApi.presets.create(data);
    return response;
  }
);

export const updatePreset = createAsyncThunk(
  "push/updatePreset",
  async ({
    id,
    data,
  }: {
    id: number;
    data: Partial<SchedulePresetCreateRequest>;
  }) => {
    const response = await pushManagementApi.presets.update(id, data);
    return { id, ...response };
  }
);

export const deletePreset = createAsyncThunk(
  "push/deletePreset",
  async (id: number) => {
    await pushManagementApi.presets.delete(id);
    return id;
  }
);

export const fetchTriggerPresets = createAsyncThunk(
  "push/fetchTriggerPresets",
  async () => pushManagementApi.triggerPresets.list()
);

export const createTriggerPreset = createAsyncThunk(
  "push/createTriggerPreset",
  async (data: TriggerPresetCreateRequest) => pushManagementApi.triggerPresets.create(data)
);

export const deleteTriggerPreset = createAsyncThunk(
  "push/deleteTriggerPreset",
  async (id: number) => {
    await pushManagementApi.triggerPresets.delete(id);
    return id;
  }
);

const pushSlice = createSlice({
  name: "push",
  initialState,
  reducers: {
    setCurrentMessage: (state, action: PayloadAction<PushMessage | null>) => {
      state.currentMessage = action.payload;
    },
    setCurrentCampaign: (state, action: PayloadAction<Campaign | null>) => {
      state.currentCampaign = action.payload;
    },
    setCurrentSegment: (state, action: PayloadAction<Segment | null>) => {
      state.currentSegment = action.payload;
    },
    setCurrentTemplate: (state, action: PayloadAction<PushTemplate | null>) => {
      state.currentTemplate = action.payload;
    },
    setCurrentTrigger: (
      state,
      action: PayloadAction<EventPushTrigger | null>
    ) => {
      state.currentTrigger = action.payload;
    },
    setCurrentSchedule: (
      state,
      action: PayloadAction<RecurringSchedule | null>
    ) => {
      state.currentSchedule = action.payload;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.total = 0;
      state.groupedItems = [];
      state.groupedTotal = 0;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCampaigns: (state) => {
      state.campaigns = [];
    },
    setCurrentPreset: (state, action: PayloadAction<SchedulePreset | null>) => {
      state.currentPreset = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendPush.pending, (state) => {
      state.isSending = true;
      state.error = null;
    });
    builder.addCase(sendPush.fulfilled, (state) => {
      state.isSending = false;
    });
    builder.addCase(sendPush.rejected, (state, action) => {
      state.isSending = false;
      state.error = action.error.message || "Failed to send push notification";
    });
    builder.addCase(fetchPushHistory.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPushHistory.fulfilled, (state, action) => {
      state.isLoading = false;
      state.messages =
        action.payload.pushes || action.payload.push_messages || [];
      state.total = action.payload.total || 0;
    });
    builder.addCase(fetchPushHistory.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch push history";
    });
    builder.addCase(fetchPushHistoryGrouped.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPushHistoryGrouped.fulfilled, (state, action) => {
      state.isLoading = false;
      state.groupedItems = action.payload.items || [];
      state.groupedTotal = action.payload.total || 0;
    });
    builder.addCase(fetchPushHistoryGrouped.rejected, (state, action) => {
      if (action.meta.aborted) return;
      state.isLoading = false;
      state.groupedItems = [];
      state.groupedTotal = 0;
      state.error = action.error.message || 'Failed to fetch grouped push history';
    });
    builder.addCase(fetchPresets.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPresets.fulfilled, (state, action) => {
      state.isLoading = false;
      state.presets = action.payload.presets || [];
    });
    builder.addCase(fetchPresets.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch presets";
    });
    builder.addCase(fetchPreset.fulfilled, (state, action) => {
      state.currentPreset = action.payload;
    });
    builder.addCase(createPreset.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createPreset.fulfilled, (state, action) => {
      state.isLoading = false;
      state.presets.unshift(action.payload.preset);
    });
    builder.addCase(createPreset.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create preset";
    });
    builder.addCase(updatePreset.fulfilled, (state, action) => {
      if (state.currentPreset?.id === action.payload.id) {
        state.currentPreset = null;
      }
    });
    builder.addCase(deletePreset.fulfilled, (state, action) => {
      state.presets = state.presets.filter((p) => p.id !== action.payload);
      if (state.currentPreset?.id === action.payload) {
        state.currentPreset = null;
      }
    });
    builder.addCase(fetchTriggerPresets.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTriggerPresets.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggerPresets = action.payload.presets || [];
    });
    builder.addCase(fetchTriggerPresets.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch trigger presets";
    });
    builder.addCase(createTriggerPreset.fulfilled, (state, action) => {
      state.triggerPresets.unshift(action.payload.preset);
    });
    builder.addCase(deleteTriggerPreset.fulfilled, (state, action) => {
      state.triggerPresets = state.triggerPresets.filter(p => p.id !== action.payload);
    });
    builder.addCase(fetchPushStatus.fulfilled, (state, action) => {
      state.statistics = action.payload;
    });
    builder.addCase(cancelScheduledPush.fulfilled, (state, action) => {
      const message = state.messages.find(
        (m) => m.message_id === action.payload.messageId
      );
      if (message) {
        message.status = "cancelled" as any;
      }
      if (state.currentMessage?.message_id === action.payload.messageId) {
        state.currentMessage.status = "cancelled" as any;
      }
    });
    builder.addCase(fetchCampaigns.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCampaigns.fulfilled, (state, action) => {
      state.isLoading = false;
      state.campaigns = action.payload.campaigns || [];
    });
    builder.addCase(fetchCampaigns.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch campaigns";
    });
    builder.addCase(fetchCampaign.fulfilled, (state, action) => {
      state.currentCampaign = action.payload;
    });
    builder.addCase(createCampaign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createCampaign.fulfilled, (state, action) => {
      state.isLoading = false;
      state.campaigns.push(action.payload.campaign);
    });
    builder.addCase(createCampaign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create campaign";
    });
    builder.addCase(updateCampaign.fulfilled, (state, action) => {
      const index = state.campaigns.findIndex(
        (c) => c.id === action.payload.campaign.id
      );
      if (index !== -1) {
        state.campaigns[index] = action.payload.campaign;
      }
      if (state.currentCampaign?.id === action.payload.campaign.id) {
        state.currentCampaign = action.payload.campaign;
      }
    });

    builder.addCase(deleteCampaign.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteCampaign.fulfilled, (state, action) => {
      state.isLoading = false;
      state.campaigns = state.campaigns.filter((c) => c.id !== action.payload);
      if (state.currentCampaign?.id === action.payload) {
        state.currentCampaign = null;
      }
    });
    builder.addCase(deleteCampaign.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to delete campaign";
    });
    builder.addCase(fetchSegments.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSegments.fulfilled, (state, action) => {
      state.isLoading = false;
      state.segments = action.payload.segments || [];
    });
    builder.addCase(fetchSegments.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch segments";
    });


    builder.addCase(fetchSegment.fulfilled, (state, action) => {
      state.currentSegment = action.payload;
      const index = state.segments.findIndex(s => s.id === action.payload.id);
      if (index !== -1) {
        state.segments[index] = action.payload;
      } else {
        state.segments.push(action.payload);
      }
    });
    builder.addCase(createSegment.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createSegment.fulfilled, (state, action) => {
      state.isLoading = false;
      state.segments.push(action.payload.segment);
    });
    builder.addCase(createSegment.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create segment";
    });

    builder.addCase(updateSegment.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(updateSegment.fulfilled, (state, action) => {
      state.isLoading = false;
      const index = state.segments.findIndex(
        (s) => s.id === action.payload.segment.id
      );
      if (index !== -1) {
        state.segments[index] = action.payload.segment;
      }
      if (state.currentSegment?.id === action.payload.segment.id) {
        state.currentSegment = action.payload.segment;
      }
    });
    builder.addCase(updateSegment.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to update segment";
    });
    builder.addCase(deleteSegment.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(deleteSegment.fulfilled, (state, action) => {
      state.isLoading = false;
      state.segments = state.segments.filter((s) => s.id !== action.payload);
      if (state.currentSegment?.id === action.payload) {
        state.currentSegment = null;
      }
    });
    builder.addCase(deleteSegment.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to delete segment";
    });
    builder.addCase(fetchTemplates.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, action) => {
      state.isLoading = false;
      state.templates = action.payload.templates || [];
    });
    builder.addCase(fetchTemplates.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch templates";
    });
    builder.addCase(fetchTemplate.fulfilled, (state, action) => {
      state.currentTemplate = action.payload;
    });
    builder.addCase(createTemplate.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createTemplate.fulfilled, (state, action) => {
      state.isLoading = false;
      state.templates.push(action.payload.template);
    });
    builder.addCase(createTemplate.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create template";
    });
    builder.addCase(updateTemplate.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteTemplate.fulfilled, (state, action) => {
      state.templates = state.templates.filter((t) => t.id !== action.payload);
      if (state.currentTemplate?.id === action.payload) {
        state.currentTemplate = null;
      }
    });
    builder.addCase(fetchTriggers.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTriggers.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers = action.payload.triggers || [];
    });
    builder.addCase(fetchTriggers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch triggers";
    });
    builder.addCase(fetchTrigger.fulfilled, (state, action) => {
      state.currentTrigger = action.payload;
    });
    builder.addCase(createTrigger.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createTrigger.fulfilled, (state, action) => {
      state.isLoading = false;
      state.triggers.push(action.payload.trigger);
    });
    builder.addCase(createTrigger.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create trigger";
    });
    builder.addCase(updateTrigger.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteTrigger.fulfilled, (state, action) => {
      state.triggers = state.triggers.filter((t) => t.id !== action.payload);
      if (state.currentTrigger?.id === action.payload) {
        state.currentTrigger = null;
      }
    });
    builder.addCase(fetchSchedules.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSchedules.fulfilled, (state, action) => {
      state.isLoading = false;
      state.schedules = action.payload.schedules || [];
    });
    builder.addCase(fetchSchedules.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to fetch schedules";
    });
    builder.addCase(fetchSchedule.fulfilled, (state, action) => {
      state.currentSchedule = action.payload;
    });
    builder.addCase(createSchedule.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(createSchedule.fulfilled, (state, action) => {
      state.isLoading = false;
      state.schedules.push(action.payload.schedule);
    });
    builder.addCase(createSchedule.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || "Failed to create schedule";
    });
    builder.addCase(updateSchedule.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(deleteSchedule.fulfilled, (state, action) => {
      state.schedules = state.schedules.filter((s) => s.id !== action.payload);
      if (state.currentSchedule?.id === action.payload) {
        state.currentSchedule = null;
      }
    });
  },
});

export const {
  setCurrentMessage,
  setCurrentCampaign,
  setCurrentSegment,
  setCurrentTemplate,
  setCurrentTrigger,
  setCurrentSchedule,
  setCurrentPreset,
  clearMessages,
  clearError,
  clearCampaigns,
} = pushSlice.actions;

export const selectPushMessages = (state: { push: PushState }) =>
  state.push.messages;
export const selectCurrentMessage = (state: { push: PushState }) =>
  state.push.currentMessage;
export const selectPushStatistics = (state: { push: PushState }) =>
  state.push.statistics;
export const selectPushTotal = (state: { push: PushState }) => state.push.total;

export const selectGroupedItems = (state: { push: PushState }) =>
  state.push.groupedItems;
export const selectGroupedTotal = (state: { push: PushState }) =>
  state.push.groupedTotal;
export const selectTriggerPresets = (state: { push: PushState }) =>
  state.push.triggerPresets;
export const selectCampaigns = (state: { push: PushState }) =>
  state.push.campaigns;
export const selectCurrentCampaign = (state: { push: PushState }) =>
  state.push.currentCampaign;
export const selectSegments = (state: { push: PushState }) =>
  state.push.segments;
export const selectCurrentSegment = (state: { push: PushState }) =>
  state.push.currentSegment;
export const selectTemplates = (state: { push: PushState }) =>
  state.push.templates;
export const selectCurrentTemplate = (state: { push: PushState }) =>
  state.push.currentTemplate;
export const selectTriggers = (state: { push: PushState }) =>
  state.push.triggers;
export const selectCurrentTrigger = (state: { push: PushState }) =>
  state.push.currentTrigger;
export const selectPresets = (state: { push: PushState }) =>
  state.push.presets;
export const selectCurrentPreset = (state: { push: PushState }) =>
  state.push.currentPreset;
export const selectSchedules = (state: { push: PushState }) =>
  state.push.schedules;
export const selectCurrentSchedule = (state: { push: PushState }) =>
  state.push.currentSchedule;
export const selectPushLoading = (state: { push: PushState }) =>
  state.push.isLoading;
export const selectPushSending = (state: { push: PushState }) =>
  state.push.isSending;
export const selectPushError = (state: { push: PushState }) => state.push.error;

export default pushSlice.reducer;
